import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';

export default function Grades() {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/employees/grades');
      if (res.ok) {
        const data = await res.json();
        setGrades(data.grades || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openEdit(grade) {
    setEditRow(grade ? {
      id: grade.id,
      name: grade.name || '',
      description: grade.description || '',
      policy: grade.policy || '',
      status: grade.status || 'Active',
    } : {
      name: '', description: '', policy: '', status: 'Active',
    });
    setEditModal(true);
  }

  async function saveGrade() {
    if (!editRow.name.trim()) return;
    try {
      await apiFetch('/api/employees/grades', {
        method: 'POST',
        body: JSON.stringify(editRow),
      });
      setEditModal(false);
      fetchData();
    } catch (err) { /* handled */ }
  }

  async function deleteGrade(id) {
    if (!confirm('Delete this grade?')) return;
    try {
      await apiFetch(`/api/employees/grades/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) { /* handled */ }
  }

  const filteredGrades = grades.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.name.toLowerCase().includes(s) ||
           (g.description || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => openEdit(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Grade
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input type="text" placeholder="Search grades..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', width: 300 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Grade</th>
              <th>Description</th>
              <th style={{ maxWidth: 300 }}>Policy</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGrades.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                {search ? 'No matching grades found' : 'No grades defined yet. Click "New Grade" to create one.'}
              </td></tr>
            ) : filteredGrades.map(g => (
              <tr key={g.id}>
                <td style={{ fontWeight: 500 }}>{g.name}</td>
                <td style={{ color: '#888' }}>{g.description || '—'}</td>
                <td style={{ color: '#888', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {g.policy ? (g.policy.length > 80 ? g.policy.substring(0, 80) + '…' : g.policy) : '—'}
                </td>
                <td>
                  <span className={`badge ${g.status === 'Active' ? 'badge-green' : 'badge-red'}`}>
                    {g.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-neon btn-sm" onClick={() => openEdit(g)} title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="btn-neon btn-sm" onClick={() => deleteGrade(g.id)} title="Delete" style={{ '--page-neon': '#ff4444' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit / Create Modal */}
      <Modal title={editRow?.id ? 'Edit Grade' : 'New Grade'} isOpen={editModal} onClose={() => setEditModal(false)} size="md">
        {editRow && <>
          <FormField label="Grade Name" name="name" required
            value={editRow.name} placeholder="e.g. G1, G2, Senior, Junior"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Description" name="description"
            value={editRow.description} placeholder="Brief description of this grade"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Policy" name="policy" type="textarea" rows={6}
            value={editRow.policy} placeholder="Grade policy details, eligibility criteria, benefits..."
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Status" name="status" type="select"
            value={editRow.status}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveGrade}>Save</button>
          </div>
        </>}
      </Modal>
    </div>
  );
}
